/**
 * Customer API response parsers
 * Transform API responses to app-friendly format
 */
import {
  normalizeOccupation,
  normalizeIncomeSlab,
  sanitizeIncomeSlabValue
} from 'utils/kyc-fatca.utils';

/**
 * Parse initiate response (signup/login initiate)
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed initiate data
 */
export const parseInitiateResponse = (response) => {
  if (!response || !response.code) {
    return {
      success: false,
      message: 'Invalid response format',
      requestId: null
    };
  }

  const { code, message, result } = response;

  return {
    success: code === 200 || code === '200',
    code: code,
    message: message || 'Request initiated successfully',
    requestId: result?.requestId || null,
    data: result || null
  };
};

/**
 * Parse confirm response (signup/login confirm)
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed confirm data with token
 */
export const parseConfirmResponse = (response) => {
  if (!response || !response.code) {
    return {
      success: false,
      message: 'Invalid response format',
      token: null
    };
  }

  const { code, message, result } = response;

  return {
    success: code === 200 || code === '200',
    code: code,
    message: message || 'Verified successfully',
    token: result?.token || null,
    data: result || null
  };
};

/**
 * Parse customer profile response
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed customer profile data
 */
export const parseCustomerProfileResponse = (response) => {
  if (!response || !response?.code) {
    return {
      success: false,
      message: 'Invalid response format',
      profile: null
    };
  }

  const { code, message, result } = response;

  // Helper function to convert boolean to Yes/No
  const booleanToYesNo = (value) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return value ?? '';
  };

  // Helper function to convert gender code to full text
  const formatGender = (gender) => {
    if (gender === 'M') return 'Male';
    if (gender === 'F') return 'Female';
    return gender ?? '';
  };

  // Display-only label for occupation option values (form state stays the value)
  const occupationLabelMapping = {
    'Public': 'Public Service',
    'Private': 'Private Service',
    'Business': 'Business',
    'Student': 'Student',
    'Housewife': 'Housewife',
    'Agriculture': 'Agriculture',
    'Retired': 'Retired',
    'Others': 'Others'
  };

  // Join non-empty address parts into a single display string
  const buildAddress = (address) => {
    if (!address) return '';
    const parts = [
      address?.addressLine1,
      address?.addressLine2,
      address?.addressLine3,
      address?.city,
      address?.state,
      address?.pincode,
      address?.country
    ]?.filter?.(Boolean) ?? [];
    return parts?.join?.(', ') ?? '';
  };

  // Extract personal details with optional chaining
  const personalDetails = result?.personalDetails ?? {};
  const customer = result?.customer ?? {};
  const addressDetails = result?.addressDetails ?? {};
  const domesticAddress = addressDetails?.domestic ?? {};
  const fatcaDetails = result?.fatcaDetails ?? {};
  const rawOccupation = fatcaDetails?.occupation ?? '';
  // BE sends "00" when the slab is not on record - treat it as missing
  const rawIncomeSlab =
    sanitizeIncomeSlabValue?.(fatcaDetails?.incomeSlab) ||
    sanitizeIncomeSlabValue?.(fatcaDetails?.income) ||
    '';

  // Normalize to the select option values used by the FATCA forms;
  // fall back to the raw API text so nothing is lost when a value is unknown
  const occupationValue = normalizeOccupation?.(rawOccupation) ?? '';
  const incomeSlabValue = normalizeIncomeSlab?.(rawIncomeSlab) ?? '';
  const occupation = occupationValue || rawOccupation || '';
  const annualIncome = incomeSlabValue || rawIncomeSlab || '';
  return {
    success: code === 200 || code === '200',
    code: code ?? '',
    message: message ?? 'Profile retrieved successfully',
    profile: {
      // Basic customer info
      emailId: customer?.email ?? '',
      mobileNumber: customer?.mobile ?? '',
      fullName: personalDetails?.fullName ?? customer?.name ?? '',
      raProfile: customer?.raProfileCode ?? '',
      customerType: customer?.customerType ?? customer?.customer_type ?? 'REGULAR',

      // Personal details
      panCardNumber: personalDetails?.panCardNumber ?? '',
      aadharNumber: personalDetails?.aadharNumber ?? '',
      fatherName: personalDetails?.fatherName ?? '',
      motherName: personalDetails?.motherName ?? '',
      gender: formatGender?.(personalDetails?.gender) ?? '',
      maritalStatus: personalDetails?.maritalStatus ?? '',
      citizenship: personalDetails?.citizenship ?? '',
      dateOfBirth: personalDetails?.dateOfBirth ?? '',

      // Address details
      addressLine1: domesticAddress?.addressLine1?.replace?.(/,/g, ', ') ?? '',
      addressLine2: domesticAddress?.addressLine2?.replace?.(/,/g, ', ') ?? '',
      addressLine3: domesticAddress?.addressLine3?.replace?.(/,/g, ', ') ?? '',
      city: domesticAddress?.city ?? '',
      state: domesticAddress?.state ?? '',
      pincode: domesticAddress?.pincode ?? '',

      // Full domestic address as a single display string
      address: buildAddress?.(domesticAddress) ?? '',

      // FATCA details — option values for forms, *Label variants for display
      placeOfBirth: fatcaDetails?.placeOfBirth ?? fatcaDetails?.birthPlace ?? '',
      occupation: occupation ?? '',
      occupationLabel: occupationLabelMapping?.[occupation ?? ''] ?? occupation ?? '',
      politicalExposure: booleanToYesNo?.(fatcaDetails?.politicalExposure) ?? '',
      taxResident: fatcaDetails?.taxResident ?? '',
      residentialStatus: fatcaDetails?.residentialStatus ?? '',
      annualIncome: annualIncome ?? '',

      // Fields not available in API (will remain empty)
      countryOfBirth: fatcaDetails?.countryOfBirth ?? domesticAddress?.country ?? '',
      sourceOfIncome: '',
      permanentAddressType: ''
    },
    data: result ?? null
  };
};

/**
 * Parse onboarding status response
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed onboarding status data
 */
export const parseOnboardingStatusResponse = (response) => {
  // Handle nested response structure (response.data.code) or direct response
  const actualResponse = response?.data || response;

  if (!actualResponse || (!actualResponse.code && !actualResponse.result)) {
    return {
      success: false,
      message: 'Invalid response format',
      onboardingStatus: null
    };
  }

  const { code, message, result } = actualResponse;

  // Check if code is success (can be number 200 or string like 'OBDS0001')
  const isSuccess = code === 200 || code === '200' ||
    (typeof code === 'string' && code.startsWith('OBDS'));

  console.log("isSuccess", result);
  return {
    success: isSuccess,
    code: code || null,
    message: message || 'Onboarding status retrieved successfully',
    onboardingStatus: result || null,
    data: result || null
  };
};

/**
 * Build PATCH /customer/profile payload for KYC FATCA mini-step
 * Sends only occupation, incomeSlab, birthPlace
 * @param {Object} formData
 * @returns {Object|null}
 */
export const buildUpdateKycProfilePayload = (formData) => {
  if (!formData) {
    return null
  }

  const birthPlace = typeof formData?.birthPlace === 'string'
    ? formData?.birthPlace?.trim?.() ?? ''
    : ''
  const occupation = typeof formData?.occupation === 'string'
    ? formData?.occupation?.trim?.() ?? ''
    : ''
  const incomeSlab = typeof formData?.incomeSlab === 'string'
    ? formData?.incomeSlab?.trim?.() ?? ''
    : ''

  if (!birthPlace || (birthPlace?.length ?? 0) < 2 || !occupation || !incomeSlab) {
    return null
  }

  return {
    occupation,
    incomeSlab,
    birthPlace
  }
}

/**
 * Parse update customer profile response
 * @param {Object} response - Raw API response
 * @returns {Object}
 */
export const parseUpdateCustomerProfileResponse = (response) => {
  if (!response) {
    return {
      success: false,
      message: 'Failed to update profile',
      data: null
    }
  }

  return {
    success: response?.success !== false && (response?.code === 200 || response?.code === '200' || !response?.code),
    code: response?.code ?? '',
    message: response?.message ?? 'Profile updated successfully',
    data: response?.result ?? response?.data ?? null
  }
}

/**
 * Parse customer error response
 * @param {Object} error - Raw error response
 * @returns {Object} Parsed error data
 */
export const parseCustomerErrorResponse = (error) => {
  if (!error) {
    return {
      success: false,
      message: 'Unknown error occurred',
      code: 'UNKNOWN_ERROR',
      details: null
    };
  }

  const { data, status } = error;

  // Handle both API error format and HTTP error format
  if (data?.code) {
    return {
      success: false,
      code: data.code,
      message: data.message || 'An error occurred',
      details: data.details || null
    };
  }

  return {
    success: false,
    message: data?.message || data?.error || 'An error occurred',
    code: data?.code || `HTTP_${status}`,
    details: data?.details || null,
    status: status || 500
  };
};

